import React from "react";
import VqqOverviewContactsField from "./VqqOverviewContactsField";
import {
  formatPlatformRoleLabel,
  pluralizeContactLabel,
} from "../utils/overviewContactsShared";
import { useVqaOverviewContacts } from "../utils/useVqaOverviewContacts";

/**
 * Assessment Overview Vendor + Sponsor contact managers (story 14580).
 * Vendor field first (matches Innovation Lab / Assessment Details prototype).
 */
export default function VqaOverviewContactsSection({
  userRole,
  assessmentDetails,
  refetch,
  activityRefetch,
  sponsorId = null,
  showVendor = true,
  showSponsor = true,
  showContactNotes = false,
  notesEditable = false,
  vendorContactNotesByEmail = {},
  sponsorContactNotesByEmail = {},
  onVendorContactNotesChange,
  onSponsorContactNotesChange,
}) {
  const {
    permissions: contactPermissions,
    savedVendorContacts,
    savedSponsorContacts,
    vendorPickerContacts,
    sponsorPickerContacts,
    vendorKnownEmails,
    sponsorKnownEmails,
    savingVendor,
    savingSponsor,
    saveContacts,
    resendInvites,
    createUser,
  } = useVqaOverviewContacts({
    userRole,
    assessmentDetails,
    refetch,
    activityRefetch,
    sponsorId,
  });

  const assignedPlatformRole = formatPlatformRoleLabel("VQA Manager");

  return (
    <>
      {showVendor && (
        <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
          <h2 className="text-sm font-medium text-gray-900">
            {pluralizeContactLabel("Vendor Contact", savedVendorContacts.length)}
          </h2>
          <div className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
            <VqqOverviewContactsField
              mode="vendor"
              entityLabel="assessment"
              contacts={savedVendorContacts}
              availableContacts={vendorPickerContacts}
              knownOrgEmails={vendorKnownEmails}
              canEdit={contactPermissions.canEditVendor}
              showPlatformRoles={contactPermissions.showVendorPlatformRoles}
              assignedPlatformRole={assignedPlatformRole}
              useAddContactLabels={contactPermissions.useAddContactLabelsOnVendor}
              saving={savingVendor}
              onSave={(draft) => saveContacts("vendor", draft)}
              onResendInvite={(targets, options) => resendInvites("vendor", targets, options)}
              onCreateUser={(form) => createUser("vendor", form)}
              showContactNotes={showContactNotes}
              notesEditable={notesEditable}
              contactNotesByEmail={vendorContactNotesByEmail}
              onContactNotesChange={onVendorContactNotesChange}
            />
          </div>
        </div>
      )}

      {showSponsor && (
        <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
          <h2 className="text-sm font-medium text-gray-900">
            {pluralizeContactLabel(
              "Sponsor Contact",
              savedSponsorContacts.length,
            )}
          </h2>
          <div className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
            <VqqOverviewContactsField
              mode="sponsor"
              entityLabel="assessment"
              contacts={savedSponsorContacts}
              availableContacts={sponsorPickerContacts}
              knownOrgEmails={sponsorKnownEmails}
              canEdit={contactPermissions.canEditSponsor}
              showPlatformRoles={contactPermissions.showSponsorPlatformRoles}
              assignedPlatformRole={assignedPlatformRole}
              saving={savingSponsor}
              onSave={(draft) => saveContacts("sponsor", draft)}
              onResendInvite={(targets, options) => resendInvites("sponsor", targets, options)}
              onCreateUser={(form) => createUser("sponsor", form)}
              showContactNotes={showContactNotes}
              notesEditable={notesEditable}
              contactNotesByEmail={sponsorContactNotesByEmail}
              onContactNotesChange={onSponsorContactNotesChange}
            />
          </div>
        </div>
      )}
    </>
  );
}
